import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import Breadcrumbs from '../components/BreadCrumbs';
import { getAIAnalytics } from '../services/aiAnalytics';
import { toast } from 'react-toastify';
import { Users, MessageSquare, Mic, Languages, Calendar, TrendingUp } from 'lucide-react';
import DateRangePicker from '../components/inputFields/DateRangePicker';
import { CustomCard } from '../components/cards/CustomCard';
import Button from '../components/buttons/Button';

// Register required Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const AIAnalytics = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async (start = null, end = null) => {
        try {
            setLoading(true);
            const response = await getAIAnalytics(start, end);
            setAnalyticsData(response.data);
        } catch (error) {
            console.error('Error fetching AI Analytics:', error);
            toast.error('Failed to fetch AI Analytics data');
        } finally {
            setLoading(false);
        }
    };

    const handleDateFilter = () => {
        if (dateRange.start && dateRange.end) {
            fetchAnalytics(dateRange.start, dateRange.end);
        } else {
            toast.warning('Please select both start and end dates');
        }
    };

    const handleReset = () => {
        setDateRange({ start: null, end: null });
        fetchAnalytics();
    };

    const StatsCard = ({ title, value, icon, color, subtitle }) => (
        <div className="group relative overflow-hidden bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-4">
                <div className="flex justify-between items-start mb-3">
                    <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                        style={{ backgroundColor: color + '20', color: color }}
                    >
                        {icon}
                    </div>
                    <div className="flex items-center gap-1 text-green-500 bg-green-50 px-2 py-1 rounded-full">
                        <TrendingUp className="w-3 h-3" />
                        <span className="text-xs font-medium">+12%</span>
                    </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                    {value.toLocaleString()}
                </div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
                {subtitle && (
                    <p className="text-xs text-gray-600 flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );

    // Generate dynamic colors for any number of languages with better visual distribution
    const generateColors = (count) => {
        // Curated color palette with good contrast and visual appeal
        const baseColors = [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
            '#FF9F40', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
            '#FECA57', '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3',
            '#FF9F43', '#10AC84', '#EE5A24', '#0984E3', '#A29BFE',
            '#FD79A8', '#FDCB6E', '#6C5CE7', '#A29BFE', '#74B9FF'
        ];

        if (count <= baseColors.length) {
            return baseColors.slice(0, count);
        }

        // Generate additional colors using HSL with better distribution
        const colors = [...baseColors];
        const goldenAngle = 137.508; // Golden angle for optimal distribution

        for (let i = baseColors.length; i < count; i++) {
            const hue = (i * goldenAngle) % 360;
            // Vary saturation and lightness for better visual distinction
            const saturation = 65 + (i % 3) * 10; // 65%, 75%, 85%
            const lightness = 45 + (i % 2) * 10;  // 45%, 55%
            colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
        }

        return colors;
    };

    // Dynamic language mapping function with comprehensive language support
    const getLanguageDisplayName = (languageCode) => {
        const languageMap = {
            // Major Indian Languages
            'en': 'English',
            'hi': 'Hindi',
            'gu': 'Gujarati',
            'ta': 'Tamil',
            'te': 'Telugu',
            'kn': 'Kannada',
            'ml': 'Malayalam',
            'bn': 'Bengali',
            'mr': 'Marathi',
            'pa': 'Punjabi',
            'or': 'Odia',
            'as': 'Assamese',
            'ne': 'Nepali',
            'ur': 'Urdu',
            'sa': 'Sanskrit',
            'ks': 'Kashmiri',
            'sd': 'Sindhi',
            'bo': 'Tibetan',
            'dz': 'Dzongkha',

            // International Languages
            'ar': 'Arabic',
            'es': 'Spanish',
            'fr': 'French',
            'de': 'German',
            'it': 'Italian',
            'pt': 'Portuguese',
            'ru': 'Russian',
            'ja': 'Japanese',
            'ko': 'Korean',
            'zh': 'Chinese',
            'th': 'Thai',
            'vi': 'Vietnamese',
            'id': 'Indonesian',
            'ms': 'Malay',
            'tl': 'Filipino',
            'sw': 'Swahili',

            // European Languages
            'af': 'Afrikaans',
            'sq': 'Albanian',
            'am': 'Amharic',
            'az': 'Azerbaijani',
            'be': 'Belarusian',
            'bs': 'Bosnian',
            'bg': 'Bulgarian',
            'ca': 'Catalan',
            'cs': 'Czech',
            'da': 'Danish',
            'et': 'Estonian',
            'eu': 'Basque',
            'fa': 'Persian',
            'fi': 'Finnish',
            'gl': 'Galician',
            'ka': 'Georgian',
            'el': 'Greek',
            'he': 'Hebrew',
            'hr': 'Croatian',
            'hu': 'Hungarian',
            'is': 'Icelandic',
            'ga': 'Irish',
            'kk': 'Kazakh',
            'ky': 'Kyrgyz',
            'lv': 'Latvian',
            'lt': 'Lithuanian',
            'mk': 'Macedonian',
            'mt': 'Maltese',
            'mn': 'Mongolian',
            'no': 'Norwegian',
            'pl': 'Polish',
            'ro': 'Romanian',
            'sk': 'Slovak',
            'sl': 'Slovenian',
            'sr': 'Serbian',
            'sv': 'Swedish',
            'tr': 'Turkish',
            'uk': 'Ukrainian',
            'uz': 'Uzbek',
            'cy': 'Welsh',
            'yi': 'Yiddish',
            'zu': 'Zulu'
        };

        // Return mapped name or formatted fallback
        return languageMap[languageCode] ||
            languageCode.charAt(0).toUpperCase() + languageCode.slice(1).toLowerCase();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="text-center py-8">
                <h2 className="text-xl font-semibold text-gray-600">
                    No analytics data available
                </h2>
            </div>
        );
    }

    const { summary, daily } = analyticsData;

    // Prepare chart data for daily messages
    const dailyChartData = {
        labels: daily.map(item => item.day),
        datasets: [
            {
                label: 'Messages',
                data: daily.map(item => item.messages),
                backgroundColor: '#36A2EB',
                borderColor: '#36A2EB',
                borderWidth: 1,
            },
            {
                label: 'Users',
                data: daily.map(item => item.users),
                backgroundColor: '#FF6384',
                borderColor: '#FF6384',
                borderWidth: 1,
            }
        ]
    };

    // Prepare pie chart data for intent distribution (dynamic)
    const intentChartData = {
        labels: summary.intent_distribution.map(item => item.intent.replace('_', ' ').toUpperCase()),
        datasets: [{
            data: summary.intent_distribution.map(item => item.count),
            backgroundColor: generateColors(summary.intent_distribution.length),
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };


    // Prepare pie chart data for language split (dynamic)
    const languageChartData = {
        labels: summary.language_split.map(item => getLanguageDisplayName(item.language)),
        datasets: [{
            data: summary.language_split.map(item => item.count),
            backgroundColor: generateColors(summary.language_split.length),
            borderWidth: 2,
            borderColor: '#fff'
        }]
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="mb-4">
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/dashboard' },
                        { name: 'AI Analytics' },
                    ]} />
                </div>

                {/* Page Header */}
                <div className="mb-6">
                    <div className="text-center lg:text-left">
                        <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
                                <TrendingUp className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                                    AI Analytics Dashboard
                                </h1>
                                <p className="text-base text-gray-600 mt-1">
                                    Monitor and analyze AI performance metrics in real-time
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Date Filter */}
                <div className="mb-6">
                    <CustomCard title={
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <Calendar className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Date Range Filter</h2>
                                <p className="text-xs text-gray-600">Select a custom date range to analyze AI performance</p>
                            </div>
                        </div>
                    }>
                        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
                            <div className="flex-1 min-w-[280px]">
                                <DateRangePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                    required
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleDateFilter}
                                    disabled={!dateRange.start || !dateRange.end}
                                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    <Calendar className="w-4 h-4" />
                                    Apply Filter
                                </Button>
                                <Button
                                    onClick={handleReset}
                                    className="flex items-center gap-2 border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </CustomCard>
                </div>

                {/* Analytics Summary */}
                <div className="mb-6">
                    <CustomCard title={
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <Languages className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Analytics Summary</h2>
                                <p className="text-xs text-gray-600">Overview of AI performance metrics</p>
                            </div>
                        </div>
                    }>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-blue-600 font-medium">Date Range</p>
                                    <p className="text-base font-bold text-blue-900">{summary.date_range}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-green-600 font-medium">Total Records</p>
                                    <p className="text-base font-bold text-green-900">{daily.length} days</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-100">
                                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                                    <MessageSquare className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-purple-600 font-medium">Total Messages</p>
                                    <p className="text-base font-bold text-purple-900">{summary.messages_sent}</p>
                                </div>
                            </div>
                        </div>
                    </CustomCard>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatsCard
                        title="New Sessions"
                        value={summary.new_sessions}
                        icon={<Users size={20} />}
                        color="#FF6384"
                        subtitle={summary.date_range}
                    />
                    <StatsCard
                        title="Active Users"
                        value={summary.active_users}
                        icon={<Users size={20} />}
                        color="#36A2EB"
                    />
                    <StatsCard
                        title="Messages Sent"
                        value={summary.messages_sent}
                        icon={<MessageSquare size={20} />}
                        color="#FFCE56"
                    />
                    <StatsCard
                        title="Voice Input Usage"
                        value={summary.voice_input_usage}
                        icon={<Mic size={20} />}
                        color="#4BC0C0"
                    />
                </div>

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                        <TrendingUp className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Daily Activity</h3>
                                        <p className="text-xs text-gray-600">Message volume and user engagement over time</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="h-64">
                                    <Bar
                                        data={dailyChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                    labels: {
                                                        usePointStyle: true,
                                                        padding: 15,
                                                        font: {
                                                            size: 11,
                                                            weight: '600'
                                                        }
                                                    }
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    grid: {
                                                        color: 'rgba(0,0,0,0.05)'
                                                    }
                                                },
                                                x: {
                                                    grid: {
                                                        display: false
                                                    }
                                                }
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
                            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                        <MessageSquare className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Intent Distribution</h3>
                                        <p className="text-xs text-gray-600">User intent analysis breakdown</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <div className="h-64">
                                    <Pie
                                        data={intentChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'bottom',
                                                    labels: {
                                                        usePointStyle: true,
                                                        padding: 12,
                                                        font: {
                                                            size: 10,
                                                            weight: '500'
                                                        }
                                                    }
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                                    <Languages className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Language Split</h3>
                                    <p className="text-xs text-gray-600">User language preference distribution</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="h-64">
                                <Pie
                                    data={languageChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                position: 'bottom',
                                                labels: {
                                                    usePointStyle: true,
                                                    padding: 12,
                                                    font: {
                                                        size: 10,
                                                        weight: '500'
                                                    }
                                                }
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Daily Breakdown</h3>
                                    <p className="text-xs text-gray-600">Detailed daily performance metrics</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="max-h-64 overflow-y-auto space-y-2">
                                {daily.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex justify-between items-center p-3 rounded-lg border border-gray-100 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:border-blue-200 transition-all duration-200 group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-200">
                                                <Calendar className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-gray-800">
                                                    {item.day}
                                                </span>
                                                <p className="text-xs text-gray-500">Daily metrics</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-4">
                                            <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded-lg">
                                                <MessageSquare className="w-3 h-3 text-blue-600" />
                                                <span className="text-xs font-semibold text-blue-800">
                                                    {item.messages}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 bg-green-50 px-2 py-1 rounded-lg">
                                                <Users className="w-3 h-3 text-green-600" />
                                                <span className="text-xs font-semibold text-green-800">
                                                    {item.users}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIAnalytics;
